import { SEASONS, type Season } from '../enums/season.enum.js'

/**
 * A calendar month, 1 through 12.
 *
 * A date can be asked for its month and the answer checked. A season setting
 * is that answer already accepted, so January is not the number 1 that also
 * means "one tree".
 */
export type CalendarMonth = number & {
  readonly __calendarMonth: 'CalendarMonth'
}

export function isCalendarMonth(value: number): value is CalendarMonth {
  return Number.isInteger(value) && value >= 1 && value <= 12
}

/** Turn a number the month check has already accepted into that month. */
export function toCalendarMonth(value: number): CalendarMonth {
  if (!isCalendarMonth(value)) {
    throw new Error('Enter a month from 1 to 12')
  }
  return value
}

/**
 * The month each season starts in. A season runs until the next one starts,
 * so the four of them cover the year with no gaps.
 *
 * Whole months, because a season is how an arborist talks about timing, not a
 * calendar the crew is booked against (docs/seasonal-work.md).
 */
export type SeasonStartMonths = Record<Season, CalendarMonth>

/** Brand four months the caller already holds as plain numbers. */
export function toSeasonStartMonths(
  starts: Record<Season, number>,
): SeasonStartMonths {
  return {
    WINTER: toCalendarMonth(starts.WINTER),
    SPRING: toCalendarMonth(starts.SPRING),
    SUMMER: toCalendarMonth(starts.SUMMER),
    FALL: toCalendarMonth(starts.FALL),
  }
}

/**
 * What an install starts with: the meteorological seasons, which is how the
 * year reads in Lubbock. Changed in Settings.
 */
export const DEFAULT_SEASON_START_MONTHS: SeasonStartMonths = toSeasonStartMonths({
  WINTER: 12,
  SPRING: 3,
  SUMMER: 6,
  FALL: 9,
})

const MONTH_ABBREVIATIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

/** How many months forward from `from` to `to`, 0–11. */
function monthsAfter(from: number, to: number): number {
  return (((to - from) % 12) + 12) % 12
}

/**
 * Whether four start months make a year: each a whole month, and in the
 * year's order (spring after winter, summer after spring, fall after summer),
 * every season at least a month long.
 *
 * Checked as a whole rather than month by month, because no single month is
 * wrong on its own. Spring starting in January is fine until winter also
 * starts in January.
 */
export function seasonStartMonthsAreValid(
  starts: Record<Season, number>,
): boolean {
  const months = SEASONS.map((season) => starts[season])
  if (!months.every((month) => Number.isInteger(month) && month >= 1 && month <= 12)) {
    return false
  }
  const spring = monthsAfter(starts.WINTER, starts.SPRING)
  const summer = monthsAfter(starts.WINTER, starts.SUMMER)
  const fall = monthsAfter(starts.WINTER, starts.FALL)
  return spring > 0 && summer > spring && fall > summer
}

/** The season a month (1–12) falls in. */
export function seasonOfMonth(month: number, starts: SeasonStartMonths): Season {
  // The season that started most recently, counting back from this month.
  let closest: Season = 'WINTER'
  let closestDistance = 12
  for (const season of SEASONS) {
    const distance = monthsAfter(starts[season], month)
    if (distance < closestDistance) {
      closest = season
      closestDistance = distance
    }
  }
  return closest
}

/** The season a calendar day (`YYYY-MM-DD`) falls in. */
export function seasonOfDay(day: string, starts: SeasonStartMonths): Season {
  return seasonOfMonth(Number(day.slice(5, 7)), starts)
}

/**
 * The four seasons in the order the year brings them, starting from this one.
 * "Fall" gives fall, winter, spring, summer.
 */
export function seasonsFrom(season: Season): Season[] {
  const start = SEASONS.indexOf(season)
  return SEASONS.map((_, offset) => SEASONS[(start + offset) % SEASONS.length] ?? season)
}

/** The months a season covers, as a reader would write them: "Dec–Feb". */
export function describeSeasonMonths(
  season: Season,
  starts: SeasonStartMonths,
): string {
  const following = seasonsFrom(season)[1] ?? season
  const first = starts[season]
  const last = ((starts[following] + 10) % 12) + 1
  const name = (month: number) => MONTH_ABBREVIATIONS[month - 1] ?? ''
  return first === last ? name(first) : `${name(first)}–${name(last)}`
}

/**
 * The first day `season` starts after `afterDay`, both `YYYY-MM-DD`.
 *
 * Seasons start on the first of their month, so this is the next 1st of that
 * month strictly after the day given: for work a visit estimated for later,
 * the day that season comes round. Only reminders read it; nobody is shown it.
 */
export function nextSeasonStart(
  season: Season,
  afterDay: string,
  starts: SeasonStartMonths,
): string {
  const year = Number(afterDay.slice(0, 4))
  const month = String(starts[season]).padStart(2, '0')
  const thisYear = `${String(year)}-${month}-01`
  return thisYear > afterDay ? thisYear : `${String(year + 1)}-${month}-01`
}

/** A `YYYY-MM-DD` day moved by a whole number of days. */
export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}
